/**
 * Analytics Charts Components
 *
 * Simplified chart components for educational analytics dashboards.
 * Uses native HTML/CSS and existing dependencies for data visualization.
 */

import React, { useMemo } from 'react';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MetricCardProps {
  title: string;
  value: number | string;
  trend?: number;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  subtitle?: string;
  format?: 'number' | 'percentage' | 'currency';
}

interface RiskDistributionProps {
  data: Array<{ name: string; value: number; color: string }>;
  title?: string;
}

interface BarChartProps {
  data: Array<{ name: string; value: number }>;
  title?: string;
  color?: string;
}

interface LineChartProps {
  data: Array<{ date: string; value: number; label?: string }>;
  title?: string;
  color?: string;
}

// Color palette for educational data
const EDUCATIONAL_COLORS = {
  at_risk: '#ef4444', // Red-500
  medium_risk: '#f97316', // Orange-500
  low_risk: '#22c55e', // Green-500
  critical: '#a855f7', // Purple-500
  interventions: '#3b82f6', // Blue-500
  benchmark: '#8b5cf6', // Violet-500
  attendance: '#06b6d4', // Cyan-500
  academic: '#6366f1', // Indigo-500
  behavioral: '#ec4899', // Pink-500
  assessment: '#8b5a2b', // Brown-600
};

// Utility function to format values
const formatValue = (
  val: number | string,
  format: 'number' | 'percentage' | 'currency' = 'number'
) => {
  if (typeof val === 'string') return val;

  switch (format) {
    case 'percentage':
      return `${val.toFixed(1)}%`;
    case 'currency':
      return `$${val.toLocaleString()}`;
    default:
      return val.toLocaleString();
  }
};

// Metric Card Component
export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  trend,
  icon,
  color = 'primary',
  subtitle,
  format = 'number',
}) => {
  const colorClasses = {
    primary: 'text-blue-600 bg-blue-50',
    secondary: 'text-gray-600 bg-gray-50',
    success: 'text-green-600 bg-green-50',
    warning: 'text-orange-600 bg-orange-50',
    error: 'text-red-600 bg-red-50',
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    return trend > 0 ? (
      <svg
        className="w-4 h-4 text-red-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 17l9.2-9.2M17 17V7m0 10H7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 text-green-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 7l9.2 9.2M17 7v10m0-10H7"
        />
      </svg>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {formatValue(value, format)}
          </h3>
          {subtitle && <p className="text-sm text-gray-500 mb-2">{subtitle}</p>}
          {trend !== undefined && (
            <div className="flex items-center">
              {getTrendIcon()}
              <span
                className={`text-sm ml-1 ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}
              >
                {Math.abs(trend)}%
              </span>
            </div>
          )}
        </div>
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

// Simple Bar Chart Component
export const SimpleBarChart: React.FC<BarChartProps> = ({
  data,
  title = 'Bar Chart',
  color = EDUCATIONAL_COLORS.interventions,
}) => {
  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="flex items-center">
            <div className="w-20 text-sm text-gray-600 flex-shrink-0">
              {item.name}
            </div>
            <div className="flex-1 mx-3">
              <div className="bg-gray-200 rounded-full h-4 relative">
                <div
                  className="h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
            </div>
            <div className="w-12 text-sm text-gray-900 text-right">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Risk Distribution Donut Chart (CSS-based)
export const RiskDistributionChart: React.FC<RiskDistributionProps> = ({
  data,
  title = 'Risk Distribution',
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const dataWithPercentages = data.map(item => ({
    ...item,
    percentage: (item.value / total) * 100,
  }));

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="flex items-center justify-center mb-4">
        <div className="relative w-32 h-32">
          {/* Simple ring chart using CSS */}
          <div
            className="w-32 h-32 rounded-full"
            style={{
              background: `conic-gradient(
                ${dataWithPercentages
                  .map((item, index) => {
                    const startAngle = dataWithPercentages
                      .slice(0, index)
                      .reduce((sum, d) => sum + d.percentage, 0);
                    const endAngle = startAngle + item.percentage;
                    return `${item.color} ${startAngle * 3.6}deg ${endAngle * 3.6}deg`;
                  })
                  .join(', ')}
              )`,
            }}
          >
            <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">{total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-2">
        {dataWithPercentages.map((item, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: item.color }}
            />
            <div className="text-sm">
              <span className="text-gray-900">{item.name}</span>
              <span className="text-gray-500 ml-1">
                ({item.value}, {item.percentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple Line Chart Component
export const SimpleTrendChart: React.FC<LineChartProps> = ({
  data,
  title = 'Trend Chart',
  color = EDUCATIONAL_COLORS.at_risk,
}) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;

  // Generate SVG path for line
  const pathData = useMemo(() => {
    if (data.length === 0) return '';

    const width = 300;
    const height = 150;
    const padding = 20;

    const points = data
      .map((item, index) => {
        const x = padding + (index * (width - 2 * padding)) / (data.length - 1);
        const y =
          height -
          padding -
          ((item.value - minValue) / range) * (height - 2 * padding);
        return `${x},${y}`;
      })
      .join(' ');

    return `M ${points.replace(' ', ' L ')}`;
  }, [data, minValue, range]);

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="relative">
        <svg width="300" height="150" className="mx-auto">
          {/* Grid lines */}
          <defs>
            <pattern
              id="grid"
              width="30"
              height="15"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 30 0 L 0 0 0 15"
                fill="none"
                stroke="#f3f4f6"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Data line */}
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {data.map((item, index) => {
            const x = 20 + (index * 260) / (data.length - 1);
            const y = 130 - ((item.value - minValue) / range) * 110;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="3"
                fill={color}
                className="hover:r-4 transition-all cursor-pointer"
              >
                <title>{`${item.date}: ${item.value}`}</title>
              </circle>
            );
          })}
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between mt-2 px-5">
          {data.map((item, index) => (
            <span key={index} className="text-xs text-gray-500">
              {new Date(item.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// At-Risk Student Card Component
export const StudentRiskCard: React.FC<{
  student: {
    student_id: string;
    student_name: string;
    grade_level: number;
    risk_probability: number;
    risk_level: 'Low' | 'Medium' | 'High' | 'Critical';
    primary_risk_factors: string[];
    recommended_interventions: string[];
    last_updated: string;
  };
  onActionClick?: (studentId: string) => void;
}> = ({ student, onActionClick }) => {
  const getRiskColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
        return 'border-green-200 bg-green-50';
      case 'medium':
        return 'border-orange-200 bg-orange-50';
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'critical':
        return 'border-purple-200 bg-purple-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getRiskBadgeColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
        return 'bg-green-500 text-white';
      case 'medium':
        return 'bg-orange-500 text-white';
      case 'high':
        return 'bg-red-500 text-white';
      case 'critical':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div
      className={`rounded-lg border-2 p-4 mb-4 ${getRiskColor(student.risk_level)}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-semibold text-gray-900">
              {student.student_name}
            </h4>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(student.risk_level)}`}
            >
              {student.risk_level}
            </span>
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Grade {student.grade_level} • Risk Score:{' '}
            {(student.risk_probability * 100).toFixed(1)}%
          </p>

          <div className="mb-3">
            <p className="text-sm font-medium text-gray-700 mb-1">
              Primary Risk Factors:
            </p>
            <div className="flex flex-wrap gap-1">
              {student.primary_risk_factors.slice(0, 3).map((factor, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-white bg-opacity-70 rounded text-xs border"
                >
                  {factor}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm font-medium text-gray-700 mb-1">
              Recommended Interventions:
            </p>
            <ul className="text-sm text-gray-600">
              {student.recommended_interventions
                .slice(0, 2)
                .map((intervention, index) => (
                  <li key={index} className="mb-1">
                    • {intervention}
                  </li>
                ))}
            </ul>
          </div>

          <p className="text-xs text-gray-500">
            Updated: {new Date(student.last_updated).toLocaleDateString()}
          </p>
        </div>

        <div className="ml-4">
          <button
            onClick={() => onActionClick?.(student.student_id)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Take Action
          </button>
        </div>
      </div>
    </div>
  );
};

// Progress Indicator Component
export const ProgressIndicator: React.FC<{
  value: number;
  max: number;
  label: string;
  color?: string;
}> = ({ value, max, label, color = EDUCATIONAL_COLORS.interventions }) => {
  const percentage = (value / max) * 100;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500">
          {value}/{max}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-300"
          style={{
            width: `${percentage}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
};

const AnalyticsCharts = {
  MetricCard,
  SimpleBarChart,
  RiskDistributionChart,
  SimpleTrendChart,
  StudentRiskCard,
  ProgressIndicator,
};

export default AnalyticsCharts;
